// The DITTO wordmark as a spinning chrome object. The outline is traced from the supplied flat
// logo (ditto-logo-shapes.json), extruded with a bevel and lit only by reflections of a small
// virtual photo studio, which is what makes polished metal read as chrome. three.js r160 (MIT) is
// embedded in the study page and loaded from a blob URL, so the study still works offline; in the
// Shopify theme it comes from the theme's assets through the import map in the layout.
// Without WebGL, or with reduced motion, the supplied chrome logo image is shown instead.
(async () => {
  const stage = document.querySelector('.logo-stage');
  const canvas = document.getElementById('logo-canvas');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const useImage = () => document.documentElement.classList.add('no-webgl');
  if (reduced.matches) { useImage(); return; }
  try {
    const embedded = document.getElementById('three-src');
    const THREE = embedded
      ? await import(URL.createObjectURL(new Blob([embedded.textContent], {type: 'text/javascript'})))
      : await import('three');
    const data = JSON.parse(document.getElementById('logo-shapes').textContent);

    const renderer = new THREE.WebGLRenderer({canvas, antialias: true, alpha: true, powerPreference: 'high-performance'});
    renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    const scene = new THREE.Scene();
    // A long lens from further away: the letters keep their proportions even when the logo is edge-on.
    const camera = new THREE.PerspectiveCamera(13, 1, 0.1, 80);

    // Studio: a dim room, a big softbox behind the camera and a few hot strips. Their reflections are
    // what the metal shows; the strips flash across the letters as they turn.
    const studio = new THREE.Scene();
    const room = new THREE.Mesh(new THREE.BoxGeometry(40, 40, 40), new THREE.MeshBasicMaterial({side: THREE.BackSide}));
    room.material.color.setScalar(0.035);
    studio.add(room);
    const strip = (w, h, strength, x, y, z) => {
      const panel = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({side: THREE.DoubleSide}));
      panel.material.color.setScalar(strength);
      panel.position.set(x, y, z); panel.lookAt(0, 0, 0);
      studio.add(panel);
    };
    strip(26, 14, 2.4, 0, 1.5, 13);  // big softbox behind the camera: bright faces head-on
    strip(22, 2.6, 7, 0, 10, 4);     // broad strip overhead
    strip(1.1, 16, 10, -9, 0, 5);    // tall strip left
    strip(1.1, 16, 8, 9, 1, 4);      // tall strip right
    strip(12, 0.8, 5, 0, -5, 9);     // low strip in front: bright lower bevels
    strip(3.5, 3.5, 14, 6, 6, 9);    // small hot spot
    strip(8, 8, 1.6, 0, 3, -12);     // soft light behind
    const pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromScene(studio, 0.03).texture;
    pmrem.dispose();

    const outline = (points, Kind) => {
      const path = new Kind();
      points.forEach(([x, y], i) => (i ? path.lineTo(x, y) : path.moveTo(x, y)));
      return path;
    };
    const shapes = data.shapes.map(({outer, holes}) => {
      const shape = outline(outer, THREE.Shape);
      shape.holes = holes.map(hole => outline(hole, THREE.Path));
      return shape;
    });
    const depth = 0.3;
    // The bevel stays thin: the gap between the i and its dot is only ~0.036 units.
    const geometry = new THREE.ExtrudeGeometry(shapes, {depth, curveSegments: 1, bevelEnabled: true, bevelThickness: 0.034, bevelSize: 0.012, bevelOffset: -0.006, bevelSegments: 5});
    geometry.translate(0, 0, -depth / 2);
    // Flat faces reflect almost one colour, so the classic chrome banding (bright top, dark horizon
    // line, lighter base) is painted on as a gradient across the wordmark's height.
    const band = document.createElement('canvas'); band.width = 4; band.height = 512;
    const paint = band.getContext('2d'), fade = paint.createLinearGradient(0, 0, 0, 512);
    [[0, '#ffffff'], [0.34, '#eef1f3'], [0.46, '#a3abb0'], [0.505, '#33383b'], [0.54, '#8d959a'], [0.7, '#e7ebed'], [0.86, '#ffffff'], [1, '#b8bfc3']].forEach(([at, colour]) => fade.addColorStop(at, colour));
    paint.fillStyle = fade; paint.fillRect(0, 0, 4, 512);
    const chromeBand = new THREE.CanvasTexture(band);
    chromeBand.colorSpace = THREE.SRGBColorSpace;
    const top = Math.max(...data.shapes.flatMap(s => s.outer.map(([, y]) => y)));
    chromeBand.repeat.set(1, 1 / (2 * top)); chromeBand.offset.set(0, 0.5);   // cap UVs are the shape's own x/y
    const faces = new THREE.MeshPhysicalMaterial({color: 0xffffff, map: chromeBand, metalness: 1, roughness: 0.08, envMapIntensity: 1.35});
    const sides = new THREE.MeshPhysicalMaterial({color: 0xc9d0d4, metalness: 1, roughness: 0.14, envMapIntensity: 1.2});
    const logo = new THREE.Mesh(geometry, [faces, sides]);
    const pivot = new THREE.Group();
    pivot.add(logo); scene.add(pivot);

    function fit() {
      const width = stage.clientWidth, height = stage.clientHeight;
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      const t = Math.tan(THREE.MathUtils.degToRad(camera.fov / 2));
      const halfWidth = 1.12, halfHeight = 0.36;
      camera.position.set(0, 0.02, Math.max(halfHeight / t, halfWidth / (t * camera.aspect)) + depth);
      camera.lookAt(0, 0, 0);
      camera.updateProjectionMatrix();
    }
    new ResizeObserver(fit).observe(stage);
    fit();

    // Spin: slower while the wordmark faces you, quicker round the back. The pointer tilts it.
    let spin = -0.5, bob = 0, frame = 0, px = 0, py = 0, tx = 0, ty = 0;
    addEventListener('pointermove', event => { tx = event.clientX / innerWidth - 0.5; ty = event.clientY / innerHeight - 0.5; }, {passive: true});
    const clock = new THREE.Clock();
    function render() {
      const dt = Math.min(clock.getDelta(), 0.05);
      const facing = Math.max(0, Math.cos(spin));
      spin += dt * (1.55 - 1.0 * facing ** 3);
      bob += dt;
      px += (tx - px) * 0.05; py += (ty - py) * 0.05;
      pivot.rotation.set(-0.1 + py * 0.3, spin + px * 0.35, px * -0.04);
      pivot.position.y = Math.sin(bob * 1.1) * 0.018;
      renderer.render(scene, camera);
      frame = requestAnimationFrame(render);
    }
    const run = () => { cancelAnimationFrame(frame); if (!document.hidden) { clock.getDelta(); frame = requestAnimationFrame(render); } };
    document.addEventListener('visibilitychange', run);
    canvas.addEventListener('webglcontextlost', () => { cancelAnimationFrame(frame); useImage(); });
    reduced.addEventListener('change', () => { if (reduced.matches) { cancelAnimationFrame(frame); useImage(); } });
    run();
    requestAnimationFrame(() => document.documentElement.classList.add('logo-live'));
  } catch (error) {
    useImage();
  }
})();
